import React, { useState } from 'react';
import { Plus } from 'lucide-react';
import type { Schutzklasse } from '../types';
import { GERAETEGRUPPEN, KORREKTUREN, berechneTragezeit, schichtLeistung } from '../tragezeit';
import { AuswahlFeld, Leerzeile, NeuZeile, TextFeld, Untertitel, ZahlFeld, ZeilenKarte } from './bausteine';

/**
 * Bearbeitung der Schutzklassen.
 *
 * ═══ WARUM DIE TRAGEZEIT HIER GERECHNET UND NICHT NUR NOTIERT WIRD ════════
 * Die Werte des Anhangs 2 der DGUV Regel 112-190 gelten fuer mittlere
 * Arbeitsschwere und etwa Raumtemperatur. Im Schwarzbereich, unter
 * Chemikalienschutzkleidung, im Sommer trifft beides nicht zu — und die
 * zulaessige Tragedauer sinkt entsprechend. Wer sie nur abschreibt, schreibt
 * die falsche ab.
 *
 * Das Ergebnis wird an der Klasse GESPEICHERT, samt der angesetzten
 * Korrekturen: im gedruckten Plan soll nachvollziehbar sein, warum hier
 * 90 statt 120 Minuten stehen.
 */

interface Props {
    klassen: Schutzklasse[];
    gesperrt: boolean;
    anlegen: (key: string) => Promise<unknown>;
    aendern: (id: string, u: Partial<Schutzklasse>) => void;
    loeschen: (id: string) => void;
}

const GRUPPEN_OPTIONEN = GERAETEGRUPPEN.map(g => ({
    wert: g.key,
    label: g.beleg === 'zu_pruefen' ? `${g.label} (Wert fehlt)` : g.label,
}));

export const SchutzklassenEditor: React.FC<Props> = ({
    klassen, gesperrt, anlegen, aendern, loeschen,
}) => (
    <>
        <NeuZeile
            platzhalter="Kennung, z. B. SK4"
            knopf="Schutzklasse"
            gesperrt={gesperrt}
            breite="w-40"
            onAnlegen={anlegen}
        />

        {klassen.length === 0 ? (
            <Leerzeile text="Noch keine Schutzklasse angelegt." />
        ) : (
            <ul className="space-y-3">
                {[...klassen].sort((a, b) => a.rang - b.rang).map(sk => (
                    <KlassenZeile
                        key={sk.id}
                        klasse={sk}
                        gesperrt={gesperrt}
                        onAendern={u => aendern(sk.id, u)}
                        onLoeschen={() => loeschen(sk.id)}
                    />
                ))}
            </ul>
        )}
    </>
);

// ═══════════════════════════════════════════════════════════════════════════

const KlassenZeile: React.FC<{
    klasse: Schutzklasse;
    gesperrt: boolean;
    onAendern: (u: Partial<Schutzklasse>) => void;
    onLoeschen: () => void;
}> = ({ klasse: sk, gesperrt, onAendern, onLoeschen }) => {
    const [gruppe, setGruppe] = useState(
        sk.tragezeit?.geraetegruppe ?? gruppeAusGeraet(sk.atemschutz.geraet),
    );
    const [korrekturen, setKorrekturen] = useState<string[]>(
        (sk.tragezeit?.korrekturen ?? [])
            .map(k => KORREKTUREN.find(x => x.grund === k.grund)?.key)
            .filter((k): k is string => !!k),
    );

    const rechnung = berechneTragezeit(gruppe, korrekturen);
    const schicht = schichtLeistung(rechnung, 480);

    return (
        <ZeilenKarte
            gesperrt={gesperrt}
            onLoeschen={onLoeschen}
            loeschTitel={`${sk.key} löschen`}
            kopf={
                <span className="flex items-center gap-2">
                    <span
                        className="w-3 h-3 rounded-sm border border-slate-300"
                        style={{ background: sk.farbe }}
                    />
                    <span className="font-black text-slate-800">{sk.key}</span>
                    <span className="text-sm text-slate-600">{sk.bezeichnung}</span>
                    <span className="text-xs text-slate-400">Rang {sk.rang}</span>
                </span>
            }
        >
            <div className="grid sm:grid-cols-4 gap-2 mb-2">
                <TextFeld
                    label="Kennung"
                    wert={sk.key}
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ key: v })}
                />
                <TextFeld
                    label="Bezeichnung"
                    wert={sk.bezeichnung}
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ bezeichnung: v })}
                />
                <ZahlFeld
                    label="Rang (höher = mehr Schutz)"
                    wert={sk.rang}
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ rang: v ?? 1 })}
                />
                <TextFeld
                    label="Farbe"
                    wert={sk.farbe}
                    platzhalter="#fb923c"
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ farbe: v })}
                />
            </div>

            <div className="mb-3">
                <TextFeld
                    label="Anwendungsbereich"
                    wert={sk.anwendungsbereich}
                    mehrzeilig
                    breit
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ anwendungsbereich: v })}
                />
            </div>

            {/* ── PSA ────────────────────────────────────────────────── */}
            <Untertitel text="Mindest-PSA" hinweis="Je Zeile ein Ausrüstungsteil, ISO-Code für den Aushang." />
            <ul className="space-y-1.5 mb-2">
                {sk.psa.map((p, i) => (
                    <li key={i} className="grid grid-cols-[1fr_6rem_auto] gap-2 items-center">
                        <TextFeld
                            wert={p.text}
                            gesperrt={gesperrt}
                            onSpeichern={v => onAendern({
                                psa: sk.psa.map((x, j) => (j === i ? { ...x, text: v } : x)),
                            })}
                        />
                        <TextFeld
                            wert={p.iso ?? ''}
                            platzhalter="M014"
                            gesperrt={gesperrt}
                            onSpeichern={v => onAendern({
                                psa: sk.psa.map((x, j) => (j === i ? { ...x, iso: v || undefined } : x)),
                            })}
                        />
                        {!gesperrt && (
                            <button
                                type="button"
                                onClick={() => onAendern({ psa: sk.psa.filter((_, j) => j !== i) })}
                                className="text-xs text-slate-400 hover:text-red-600"
                            >
                                entfernen
                            </button>
                        )}
                    </li>
                ))}
            </ul>
            {!gesperrt && (
                <button
                    type="button"
                    onClick={() => onAendern({ psa: [...sk.psa, { text: '' }] })}
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-stone-700 underline mb-3"
                >
                    <Plus className="w-3 h-3" /> PSA-Zeile
                </button>
            )}

            {/* ── Atemschutz nach TRGS 524 Anlage 8 ──────────────────── */}
            <Untertitel
                text="Atemschutz (TRGS 524 Anlage 8)"
                hinweis="„Geeigneter Filteratemschutz“ ist keine Festlegung, sondern deren Vermeidung."
            />
            <div className="grid sm:grid-cols-4 gap-2 mb-3">
                <TextFeld
                    label="Gerät"
                    wert={sk.atemschutz.geraet ?? ''}
                    platzhalter="Vollmaske"
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ atemschutz: { ...sk.atemschutz, geraet: v } })}
                />
                <TextFeld
                    label="Filtertyp"
                    wert={sk.atemschutz.filtertyp ?? ''}
                    platzhalter="P3 / A2P3 / Hg-P3"
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ atemschutz: { ...sk.atemschutz, filtertyp: v } })}
                />
                <TextFeld
                    label="Anzugtyp"
                    wert={sk.schutzkleidung.typ ?? ''}
                    platzhalter="5/6"
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ schutzkleidung: { ...sk.schutzkleidung, typ: v } })}
                />
                <TextFeld
                    label="Handschuhmaterial"
                    wert={sk.handschutz.material ?? ''}
                    platzhalter="Nitril"
                    gesperrt={gesperrt}
                    onSpeichern={v => onAendern({ handschutz: { ...sk.handschutz, material: v } })}
                />
            </div>

            {/* ── Tragezeit ──────────────────────────────────────────── */}
            <Untertitel
                text="Tragezeitbegrenzung (DGUV Regel 112-190 Anhang 2)"
                hinweis="Die Basiswerte gelten für mittlere Arbeitsschwere und etwa Raumtemperatur."
            />
            <div className="flex flex-wrap gap-3 items-start mb-2">
                <span className="w-64">
                    <AuswahlFeld
                        label="Gerätegruppe"
                        wert={gruppe}
                        optionen={GRUPPEN_OPTIONEN}
                        gesperrt={gesperrt}
                        onSpeichern={setGruppe}
                    />
                </span>
                <div className="flex flex-wrap gap-2 pt-4">
                    {KORREKTUREN.map(k => (
                        <label key={k.key} className="inline-flex items-center gap-1.5 text-xs text-slate-700">
                            <input
                                type="checkbox"
                                disabled={gesperrt}
                                checked={korrekturen.includes(k.key)}
                                onChange={e => setKorrekturen(v =>
                                    e.target.checked ? [...v, k.key] : v.filter(x => x !== k.key))}
                            />
                            {k.grund.split(' (')[0]}
                        </label>
                    ))}
                </div>
            </div>

            <div className="rounded-lg bg-slate-50 border border-slate-200 p-2.5">
                {rechnung.verwendbar ? (
                    <>
                        <p className="text-sm font-bold text-slate-800">
                            {rechnung.ergebnis_min} Minuten Einsatz, danach {rechnung.erholung_min} Minuten Erholung
                            <span className="font-normal text-slate-600">
                                {' '}· {schicht.einsaetze} Einsätze je Schicht,
                                {' '}{Math.round(schicht.minderung * 100)} % Minderung
                            </span>
                        </p>
                        {!gesperrt && (
                            <button
                                type="button"
                                onClick={() => onAendern({
                                    tragezeit: {
                                        geraetegruppe: gruppe,
                                        basis_min: rechnung.basis_min ?? undefined,
                                        korrekturen: rechnung.angewandte_korrekturen,
                                        ergebnis_min: rechnung.ergebnis_min ?? undefined,
                                        erholung_min: rechnung.erholung_min ?? undefined,
                                        einsaetze_pro_schicht: schicht.einsaetze,
                                        quelle: 'DGUV Regel 112-190 Anhang 2',
                                    },
                                })}
                                className="mt-1 text-[11px] font-bold text-stone-700 underline"
                            >
                                an dieser Schutzklasse speichern
                            </button>
                        )}
                    </>
                ) : (
                    <p className="text-sm text-amber-800">{rechnung.hinweise[0]}</p>
                )}
                {[...rechnung.hinweise.slice(rechnung.verwendbar ? 0 : 1), ...schicht.hinweise].map((h, i) => (
                    <p key={i} className="text-[11px] text-slate-500 mt-1">{h}</p>
                ))}
                {sk.tragezeit?.ergebnis_min && (
                    <p className="text-[11px] text-emerald-700 mt-1">
                        Gespeichert: {sk.tragezeit.ergebnis_min} min Einsatz,
                        {' '}{sk.tragezeit.erholung_min} min Erholung.
                    </p>
                )}
            </div>
        </ZeilenKarte>
    );
};

/** Ratet die Geraetegruppe aus der Geraetebezeichnung — nur als Vorbelegung. */
const gruppeAusGeraet = (geraet?: string): string => {
    const g = (geraet ?? '').toLowerCase();
    if (g.includes('vollmaske')) return 'filter_vollmaske';
    if (g.includes('gebläse') || g.includes('haube')) return 'geblaese_haube';
    if (g.includes('halbmaske')) return 'filter_halbmaske';
    if (g.includes('schlauch')) return 'isolier_schlauch';
    if (g.includes('behälter')) return 'isolier_behaelter';
    return 'filter_halbmaske';
};
